import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { QuestionVisibility, SimpleEqualityQuestionVisibility, SurveyForm, SurveyQuestion } from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import React, { useCallback, useMemo, useState } from 'react';
import { DragDropContext, Droppable, DroppableProvided, DropResult } from 'react-beautiful-dnd';
import { IModalContext, ModalChildComponentContextKey } from '../../../../../../models';
import { ErrorMessagesViewModelKey, IErrorMessageComponentProps } from '../../../../../../models/AppState';
import { IFormField } from '../../../../../../viewmodels/AppViewModels';
import { Button } from '../../../../../components-v2/Button';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { asModalComponent } from '../../../../../components/Modal';
import { baseStyleSheet } from '../../../../../styles/styles';

import { ReorderQuestionItem } from './ReorderQuestionItem';
import { styleSheet } from './styles';

interface IReorderQuestionsResult {
	reorderedQuestions?: IFormField<string>[];
	/** Field IDs whose visibleWhen should be cleared because reordering broke the dependency */
	clearedVisibilityFieldIds?: string[];
	canceled?: boolean;
}

interface IProps
	extends IEventLoggingComponentProps,
		IModalContext<IReorderQuestionsResult>,
		IErrorMessageComponentProps {
	questions: IFormField<string>[];
	surveyId: string;
	impersonationContext?: any;
	className?: string;
	styles?: StyleDeclarationValue[];
	/** Optional surveyForm for computing visibility dependency labels */
	surveyForm?: SurveyForm;
}

/**
 * Extracts the fieldId that a question depends on from its visibleWhen condition.
 *
 * NOTE: Current business rule is that LogicalQuestionVisibility (OR) always references
 * the same parent question across all its child visibilities. So we only need to extract
 * the fieldId from the first child. If this business rule changes, this function will
 * need to return multiple fieldIds.
 */
const getDependsOnFieldId = (visibleWhen: QuestionVisibility): string | null => {
	if (
		visibleWhen._type === 'SimpleEqualityQuestionVisibility' ||
		visibleWhen._type === 'ComparisonQuestionVisibility'
	) {
		return (visibleWhen as SimpleEqualityQuestionVisibility).fieldId;
	}
	if (visibleWhen._type === 'LogicalQuestionVisibility') {
		const logical = visibleWhen as { visibilities?: QuestionVisibility[] };
		if (logical.visibilities?.length) {
			return getDependsOnFieldId(logical.visibilities[0]);
		}
	}
	return null;
};

/**
 * Builds a map of fieldId → dependsOnFieldId from surveyForm.
 * Used to show "Depends on Question X" labels in the reorder modal.
 */
const buildDependencyMap = (surveyForm?: SurveyForm): Map<string, string> => {
	const map = new Map<string, string>();
	if (!surveyForm?.surveyParts) return map;

	for (const part of surveyForm.surveyParts) {
		const question = part as SurveyQuestion;
		if (!question.fieldId || !question.visibleWhen) continue;
		const dependsOn = getDependsOnFieldId(question.visibleWhen);
		if (dependsOn) {
			map.set(question.fieldId, dependsOn);
		}
	}
	return map;
};

/**
 * Inner presentational component, exported for unit testing without the
 * inject/observer/withEventLogging HOC chain. `parentModal` can be passed directly.
 */
export const _ReorderQuestionsModal: React.FC<IProps> = props => {
	const { questions: propsQuestions, surveyId, parentModal, surveyForm } = props;

	// Reorder is a local form change (persisted on Save), so EVERY question with an id is
	// reorderable — including unsaved ones (which lack `_type`) and ones with blank labels
	// (rendered as "Untitled Question"). Mirrors the V2 reorder behavior.
	const reorderableQuestions = propsQuestions.filter(question => !!question.id);

	const [questions, setQuestions] = useState<IFormField<string>[]>(reorderableQuestions);
	const [_saving, _setSaving] = useState(false);

	// Mutable dependency map: fieldId → dependsOnFieldId. Entries are removed when reordering breaks them.
	const [dependencyMap, setDependencyMap] = useState(() => buildDependencyMap(surveyForm));
	// Track field IDs whose visibleWhen was cleared by reordering, so parent can update surveyForm
	const [clearedVisibilityFieldIds, setClearedVisibilityFieldIds] = useState<string[]>([]);

	const dependencyLabels = useMemo(() => {
		const labels = new Map<string, string>();
		if (dependencyMap.size === 0) return labels;

		for (const question of questions) {
			const dependsOnFieldId = dependencyMap.get(question.id);
			if (!dependsOnFieldId) continue;

			const parentIndex = questions.findIndex(q => q.id === dependsOnFieldId);
			if (parentIndex >= 0) {
				labels.set(question.id, `Depends on Question ${parentIndex + 1}`);
			}
		}
		return labels;
	}, [questions, dependencyMap]);

	// No effects needed: the modal mounts fresh each time it opens,
	// so useState initializers handle setup. Props don't change while open.

	const onDragEnd = useCallback(
		(result: DropResult) => {
			if (!result.destination || result.source.index === result.destination.index) return;

			const newQuestions = [...questions];
			const [removed] = newQuestions.splice(result.source.index, 1);
			newQuestions.splice(result.destination.index, 0, removed);

			// Check for broken dependencies: a dependent must appear after its parent
			const positionMap = new Map(newQuestions.map((q, i) => [q.id, i]));
			const brokenFieldIds: string[] = [];

			for (const [fieldId, dependsOnFieldId] of dependencyMap) {
				const dependentIdx = positionMap.get(fieldId);
				const parentIdx = positionMap.get(dependsOnFieldId);
				if (dependentIdx !== undefined && parentIdx !== undefined && dependentIdx <= parentIdx) {
					brokenFieldIds.push(fieldId);
				}
			}

			if (brokenFieldIds.length > 0) {
				setDependencyMap(prev => {
					const next = new Map(prev);
					for (const id of brokenFieldIds) {
						next.delete(id);
					}
					return next;
				});
				setClearedVisibilityFieldIds(prev => [...prev, ...brokenFieldIds]);
			}

			setQuestions(newQuestions);
		},
		[questions, dependencyMap]
	);

	const onCancel = () => {
		if (parentModal) {
			parentModal.onRequestClose(undefined, true);
		}
	};

	const onSaveClicked = () => {
		if (parentModal) {
			parentModal.onRequestClose({ reorderedQuestions: questions, clearedVisibilityFieldIds }, false);
		}
	};

	const droppableId = `reorder-questions-${surveyId}`;

	if (reorderableQuestions.length === 0) {
		return (
			<div className={css(styleSheet.boardHeader)}>
				<div className={css(styleSheet.boardHeaderTitles)}>
					<div className={css(styleSheet.boardHeaderTitle)}>No questions to reorder</div>
					<div className={css(styleSheet.boardHeaderSubheader)}>There are no questions available to reorder.</div>
				</div>
				<div className={css(styleSheet.boardFooter)}>
					<Button variant='secondary' onClick={onCancel}>
						Close
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className={css(styleSheet.container)}>
			<DragDropContext onDragEnd={onDragEnd}>
				<div className={css(styleSheet.boardHeader)}>
					<div className={css(styleSheet.boardHeaderTitles)}>
						<div className={css(styleSheet.boardHeaderTitle)}>Reorder Questions</div>
						<div className={css(styleSheet.boardHeaderSubheader)}>Drag and drop questions to reorder them.</div>
					</div>
				</div>
				<div className={css(styleSheet.body)}>
					<Droppable direction='vertical' droppableId={droppableId}>
						{(droppableProvided: DroppableProvided) => (
							<div
								className={css(styleSheet.columns)}
								ref={droppableProvided.innerRef}
								{...droppableProvided.droppableProps}
							>
								{questions.map((question, index) => (
									<ReorderQuestionItem
										key={question.id}
										question={question}
										index={index}
										dependencyLabel={dependencyLabels.get(question.id)}
									/>
								))}
								{droppableProvided.placeholder}
							</div>
						)}
					</Droppable>
				</div>
				<div className={css(styleSheet.boardFooter)}>
					<Button variant='primary' onClick={onSaveClicked}>
						Update Order
					</Button>
					<Button variant='secondary' onClick={onCancel}>
						Cancel
					</Button>
				</div>
				{_saving && <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />}
			</DragDropContext>
		</div>
	);
};

const ReorderQuestionsModalAsObserver = observer(_ReorderQuestionsModal);
const ReorderQuestionsModalWithContext = inject(
	ErrorMessagesViewModelKey,
	ModalChildComponentContextKey
)(ReorderQuestionsModalAsObserver);
const ReorderQuestionsModal = withEventLogging(ReorderQuestionsModalWithContext, 'ReorderQuestionsModal');

export const ReorderQuestionsModalComponent = asModalComponent(ReorderQuestionsModal, {
	className: css(styleSheet.modal),
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
});
